import { useRouter } from 'next/router';
import { InferGetStaticPropsType } from 'next';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { BaseFramePDP, Layout } from '@components';
import {
	BaseFrameReturn,
	BUILD_FLOW_STEPS,
	CUSTOMER_JOURNEYS,
	FRAME_COLOR_HANDLES,
	FRAME_COLORS,
	getBaseFrameProps,
	getBaseName,
	getContextFromBundle,
	getParsedHandleandColor,
	LARGE_WIDTH,
	LENS_COLORS,
	LOCALE_CODES,
	NEW_BASE_FRAME_NAMES,
	useTranslation,
	withLocalizedPaths,
} from '@utils/index';
import { useCart } from '@services/shopify';
import { NormalizedCollection } from '@ts/product';
import { BFProvider } from '@context';
import { BUNDLE_KEY_QUERY } from '@utils/constants/cart';
import { BASE_FRAME_LENS_OPTIONS } from '@utils/constants/base-skus';
import { useIsMobile } from '@utils/hooks';

const getTranslatedTexts = translator => {
	return {
		titleEyeglasses: (productName: string) => translator('seo-metadata-title-eyeglasses', { productName }),
	};
};

const Eyeglass = ({
	bfCollectionListSlug,
	prescriptionOptions,
	product,
	yotpo,
	variantPrices,
	subscriptionProducts,
	materialOptions,
}: InferGetStaticPropsType<typeof getStaticProps> & { collection: NormalizedCollection }) => {
	const isMobile = useIsMobile({ maxWidth: LARGE_WIDTH });
	const isQuickAdd = useFeatureIsOn('is-quick-add') && !isMobile;
	const { query } = useRouter();
	const { data: cart } = useCart();
	const isV2Subscription = useFeatureIsOn('is-v2-subscription-test');
	const savedBundle = cart?.bundles && query[BUNDLE_KEY_QUERY] ? cart?.bundles[`${query[BUNDLE_KEY_QUERY]}`] : null;
	const { color, tops, lenses, prescription, readerStrength, lensPackage, sellingPlanId, productSubscriptionId } =
		getContextFromBundle({
			bundle: savedBundle,
			prescriptionOptions: prescriptionOptions,
			frameColor: query.frameColor as string,
			baseColor: query['base_color'] as string,
			isV2Subscription,
		});
	const frame = getBaseName(product.name, false) as NEW_BASE_FRAME_NAMES;
	const { translator } = useTranslation();
	const translations = getTranslatedTexts(translator);

	return (
		<BFProvider
			key={frame}
			props={{
				collectionListSlug: bfCollectionListSlug,
				frame: frame,
				frameColor: color as FRAME_COLORS,
				lensColor: query.lensColor as LENS_COLORS,
				journey: CUSTOMER_JOURNEYS.EYEGLASSES,
				product,
				rxOptions: prescriptionOptions,
				tops,
				readerRx: readerStrength,
				variantImages: product.variantImages[color].splice(0, -1),
				step: isQuickAdd ? BUILD_FLOW_STEPS.LENS : BUILD_FLOW_STEPS.COLOR,
				rxType: prescription,
				variantPrices: variantPrices,
				selectedLenses: lenses?.includes(BASE_FRAME_LENS_OPTIONS.LIGHT_RESPONSIVE)
					? [...lenses, BASE_FRAME_LENS_OPTIONS.BLUE_LIGHT]
					: lenses,
				lensPackage,
				subscriptionProducts: subscriptionProducts,
				sellingPlanId: sellingPlanId,
				productSubscriptionId: productSubscriptionId,
				materialOptions,
			}}
		>
			<BaseFramePDP
				seoMetadata={{ title: translations.titleEyeglasses(product.name) }}
				journey={CUSTOMER_JOURNEYS.EYEGLASSES}
				yotpo={yotpo}
			/>
		</BFProvider>
	);
};

export const getStaticPaths = withLocalizedPaths(async () => {
	const paths = [];

	return {
		paths,
		fallback: 'blocking',
	};
});

export async function getStaticProps(args) {
	const {
		params: { demo, handle },
		locale,
	} = args;
	if (Object.values(FRAME_COLOR_HANDLES).some(colorHandle => handle.includes(colorHandle))) {
		const { handle: parsedHandle, color } = getParsedHandleandColor(handle);

		return {
			redirect: {
				permanent: true,
				destination: `${locale && locale !== LOCALE_CODES.US ? `/${locale}` : ''}/eyeglasses/${demo}/${parsedHandle}?frameColor=${color}`,
			},
		};
	}

	try {
		const props: BaseFrameReturn = await getBaseFrameProps({
			demo,
			handle,
			category: 'eyeglasses',
			locale,
		});

		return {
			props,
			revalidate: 600,
		};
	} catch (error) {
		console.error(`SSG error in /eyeglasses/${demo}/${handle}: ${error}`);
		return {
			notFound: true,
			revalidate: 600,
		} as const;
	}
}

Eyeglass.getLayout = function getLayout(page, pageProps) {
	return <Layout pageProps={{ ...pageProps, background: 'gray0' }}>{page}</Layout>;
};

export default Eyeglass;
